import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, TrendingUp, ArrowUpRight } from 'lucide-react';

const pct = (v) => (v == null ? '–' : `${Math.round(Number(v) * 100)}%`);

const STATUS_STYLES = {
  actionable: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  monitor: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

const ProbabilityBar = ({ probs, testId }) => {
  const segs = [
    { k: 'home', cls: 'bg-[#3B82F6]' },
    { k: 'draw', cls: 'bg-gray-500' },
    { k: 'away', cls: 'bg-amber-400' },
  ];
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-[#1e2a45]" data-testid={testId}>
      {segs.map((s) => (
        <div key={s.k} className={`${s.cls} transition-[width] duration-700`} style={{ width: `${(probs?.[s.k] || 0) * 100}%` }} />
      ))}
    </div>
  );
};

const kickoffLabel = (iso) =>
  new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' });

export const PickCard = ({ pick, index }) => {
  const probs = pick.fair_probabilities || {};
  const odds = pick.fair_odds || {};
  const edge = pick.value?.edge;
  return (
    <article
      data-testid={`pick-card-${pick.id}`}
      className="group rounded-2xl border border-[#1e2a45] bg-[#0d1424] p-5 hover:border-[#3B82F6]/50 transition-colors duration-300 animate-in fade-in slide-in-from-bottom-2"
      style={{ animationDelay: `${index * 40}ms`, animationFillMode: 'both' }}
    >
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-gray-400 uppercase tracking-wider truncate" data-testid={`pick-league-${pick.id}`}>{pick.league}</span>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 capitalize ${STATUS_STYLES[pick.status] || STATUS_STYLES.monitor}`} data-testid={`pick-status-${pick.id}`}>
          {pick.status}
        </span>
      </div>
      <h3 className="mt-3 text-lg font-semibold text-white leading-snug" data-testid={`pick-teams-${pick.id}`}>
        {pick.home} <span className="text-gray-500 font-normal">vs</span> {pick.away}
      </h3>
      <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
        <Clock className="h-3.5 w-3.5" /> {kickoffLabel(pick.kickoff_utc)}
      </div>
      <div className="mt-4 space-y-2">
        <ProbabilityBar probs={probs} testId={`pick-prob-bar-${pick.id}`} />
        <div className="grid grid-cols-3 text-xs">
          <span className="text-[#93b4f5]">Home {pct(probs.home)} <span className="text-gray-500">@{odds.home ?? '–'}</span></span>
          <span className="text-center text-gray-300">Draw {pct(probs.draw)} <span className="text-gray-500">@{odds.draw ?? '–'}</span></span>
          <span className="text-right text-amber-300">Away {pct(probs.away)} <span className="text-gray-500">@{odds.away ?? '–'}</span></span>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#1e2a45] pt-4">
        <span className="flex items-center gap-1.5 text-xs text-gray-400" data-testid={`pick-edge-${pick.id}`}>
          <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
          {edge != null ? `${(edge * 100).toFixed(1)}% edge${pick.value?.bookmaker ? ` · ${pick.value.bookmaker}` : ''}` : 'Fair price'}
        </span>
        <Link
          to={`/?market=market_tipsaudit_${pick.id}`}
          data-testid={`pick-trade-link-${pick.id}`}
          className="inline-flex items-center gap-1 rounded-full bg-[#3B82F6] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2563eb] transition-colors"
        >
          Trade on AMP <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </article>
  );
};

export default PickCard;
